import React, { useEffect } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { COLORS } from 'apps/web_forms/utils/constants';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

const StyledDisableInput = styled.input`
  &.read-only.input-disabled {
    cursor: default;
    &:active {
      border: 1px solid ${COLORS.borderGrey};
    }
  }
`;

const CalculateInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    title,
    identifier,
    hint,
    hidden,
    validate_msg: validateMsg,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const [{ isActive, calculatedResult, isElementValid }] = useRules(element, formContext);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
    });
  }, [isElementValid]);

  useEffect(() => {
    changeValue(calculatedResult);
  }, [calculatedResult]);

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <label htmlFor={identifier}>{title}</label>
      <StyledDisableInput
        type='text'
        value={`${calculatedResult !== undefined ? calculatedResult : ''}`}
        name={identifier}
        id={identifier}
        readOnly
        className={clsx(
          'read-only',
          'input-disabled',
          !isElementValid && 'error',
          hasResourceUpdateHighlight && 'updateHighlight'
        )}
        onFocus={removeResourceUpdateHighlight}
        onClick={removeResourceUpdateHighlight}
      />
      {hint && <p className='element-hint'>{hint}</p>}
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

CalculateInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    hint: PropTypes.string,
    validate_msg: PropTypes.string,
    hidden: PropTypes.bool,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(CalculateInput);
